import React, { useEffect, useRef, useState } from 'react';
import ReactInstaStories from 'react-insta-stories';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { dataLimits, S3_URL, S3_SMALL_THUMB } from 'constants/index';
import { getTimeDifferenceInBaseShortUnit, getFileName } from 'utils';
import {
	useDeleteStoryMutation,
	useReportMutation,
	useStoryViewedMutation,
	useStoryViewersQuery,
} from 'services';
import { Col, Image, Row, Stack, Dropdown } from 'react-bootstrap';
import Loader from 'components/Common/Loader';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useQueryClient } from '@tanstack/react-query';
import useCommonStore, { setModalConfig } from 'store/common';
import useAuthStore from 'store/auth';
import useTranslate from 'hooks/useTranslate';
import './styles.scss';
import { useNavigate } from 'react-router-dom';

const InstaStory: React.FC<any> = ({
	isActive,
	storyData,
	slideLength,
	activeSlide,
	goToNextSlide,
	customSlideChange,
	slideIndex,
}) => {
	const { translate } = useTranslate();
	const queryClient = useQueryClient();
	const { user: authUser }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});
	const { hideCommonModal }: any = useCommonStore((state) => state);

	// Add safety checks for storyData
	if (!storyData) {
		console.warn('InstaStory: storyData is undefined');
		return (
			<div className="story-preview-box-stories position-relative w-100 h-100 d-flex align-items-center justify-content-center">
				<p className="text-white">Story data not available</p>
			</div>
		);
	}

	const { fullName, profilePicture, story: storiesArr } = storyData;
	const [stories, setStories] = useState([]);
	const [currentPage, setCurrentPage] = useState(0);
	const [storyId, setStoryId] = useState(null);
	const [usersList, setUsersList] = useState<any>([]);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const visibleIndex = useRef(0);
	const navigate = useNavigate();

	const { mutateAsync: reportProfileMutation } = useReportMutation();
	const { mutateAsync: StoryViewedMutation } = useStoryViewedMutation();
	const { mutateAsync: deleteStoryMutation } = useDeleteStoryMutation();

	const searchString = `order=DESC&page=${currentPage}&limit=${dataLimits?.storyViewers}&hasExpired=false&inActive=false&type=all&socialPostType=all&connectionType=connected`;
	const { data: { docs: viewers = [], meta = {} } = {} } = useStoryViewersQuery({
		searchString,
		id: storyId,
	});

	useEffect(() => {
		// Ensure we always have a valid array and filter out any invalid stories
		const validStories = (storiesArr || []).filter(
			(story: any) => story && story.mediaItem && typeof story.mediaItem === 'string',
		);
		setStories(validStories);
	}, [storiesArr]);

	useEffect(() => {
		if (storyId) {
			queryClient.invalidateQueries({
				queryKey: ['get-story-viewers', { id: storyId, searchString }],
			});
		}
	}, [currentPage]);

	const updateTotalViews = (newTotalViews: number) => {
		const updatedStories: any = stories.map((story: any) =>
			story.id === storyId ? { ...story, totalViews: newTotalViews } : story,
		);
		setStories(updatedStories);
	};

	useEffect(() => {
		if (viewers?.length > 0) {
			const timerId = setTimeout(() => {
				setUsersList([...usersList, ...viewers]);
				updateTotalViews(meta?.totalDocs || 0);
			}, 100);

			return () => clearTimeout(timerId);
		}
	}, [viewers]);

	useEffect(() => {
		if (isActive) {
			visibleIndex.current = 0;
			setSelectedIndex(0);
		}
	}, [activeSlide]);

	const loadMoreFunc = () => {
		setCurrentPage((prev) => prev + 1);
	};

	const resetStates = () => {
		setStoryId(null);
		setCurrentPage(0);
		setUsersList([]);
	};

	const onAllStoriesEndHandler = () => {
		if (activeSlide + 1 === slideLength) {
			hideCommonModal();
		} else {
			goToNextSlide();
		}
	};

	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 1199); // Mobile threshold
		};
		handleResize(); // Set initial state
		window.addEventListener('resize', handleResize);

		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const storyContent = {
		width: isMobile ? '100vw' : '410px',
		height: isMobile ? 'auto' : '670px',
		maxWidth: '100%',
		maxHeight: '100%',
		margin: 'auto',
		objectFit: 'contain',
		objectPosition: 'center',
	};

	const onStoryStart = (index: number) => {
		// Add bounds checking to prevent accessing undefined stories
		if (!stories || index < 0 || index >= stories.length) {
			console.warn('Invalid story index:', index, 'Stories length:', stories?.length);
			return;
		}

		const storyData: any = stories[index];
		if (!storyData) {
			console.warn('Story data not found at index:', index);
			return;
		}

		// Only call StoryViewedMutation if user is authenticated and viewing someone else's story
		const { token }: Record<string, any> = useAuthStore.getState().userInfo ?? {};
		const isAuthenticated = !!(token && authUser?.userId);

		if (isAuthenticated && storyData?.userId !== authUser?.userId && storyData?.id) {
			StoryViewedMutation({ storyId: storyData.id })
				.then((res: any) => {
					if (res?.success) {
						visibleIndex.current = index;
					}
				})
				.catch((error: any) => {
					console.error('Error in StoryViewedMutation:', error);
				});
		}
	};

	const onNext = () => {
		const i = visibleIndex.current + 1;
		visibleIndex.current = i;
		setSelectedIndex(i);
	};

	const onPrevious = () => {
		const i = visibleIndex.current - 1;
		visibleIndex.current = i;
		setSelectedIndex(i);
	};

	const onDeleteStory = (storyId: number) => {
		deleteStoryMutation(storyId)
			.then(() => {
				queryClient.invalidateQueries({
					queryKey: ['get-stories'],
				});
			})
			.finally(() => {
				hideCommonModal();
			});
	};

	const getStoryArr = () => {
		if (stories?.length > 0) {
			const storiesData = stories
				?.filter((story: any) => story?.mediaItem) // Filter out invalid stories first
				?.map((story: any) => {
					return {
						url: S3_URL + story?.mediaItem,
						type: story?.mediaType || 'image',
						header: {
							heading: fullName || '',
							subheading: getTimeDifferenceInBaseShortUnit(story?.createdAt),
							profileImage: profilePicture ? S3_URL + profilePicture : IMAGE_PATHS.UserDefault,
						},
						duration: 10000,
						seeMore: ({ close }: any) => {
							// Check if user is authenticated
							const { token }: Record<string, any> = useAuthStore.getState().userInfo ?? {};
							const isAuthenticated = !!(token && authUser?.userId);

							return (
								<div
									onClick={() => {
										close();
										resetStates();
									}}
								>
									<div className="viewers-list w-100">
										<div className="viewers-list-head d-flex align-items-center justify-content-between">
											<h5 className="mb-0 text-white">View Stories</h5>
											<span
												className="d-flex justify-content-center view-btn m-0"
												onClick={() => {
													resetStates();
													close();
												}}
											>
												<i className="ri-eye-fill"></i>
												{story?.totalViews || 0}
											</span>
										</div>
										{isAuthenticated ? (
											<div id={`infiniteDiv-viewers-list`} className="viewers-list-scroll">
												<InfiniteScroll
													dataLength={usersList?.length}
													next={loadMoreFunc}
													hasMore={meta?.hasNextPage}
													loader={
														usersList?.length >= 1 && (
															<div className="d-flex justify-content-center align-items-center">
																<Loader />
															</div>
														)
													}
													scrollableTarget={`infiniteDiv-viewers-list`}
													scrollThreshold="300px"
													refreshFunction={loadMoreFunc}
													pullDownToRefreshThreshold={50}
													pullDownToRefreshContent={
														<h3 style={{ textAlign: 'center' }}>
															&#8595; {translate('Common.pullDownToRefresh')}
														</h3>
													}
													releaseToRefreshContent={
														<h3 style={{ textAlign: 'center' }}>
															&#8593; {translate('Common.releaseToRefresh')}
														</h3>
													}
													className="viewers-list-body"
												>
													<Row className="m-0">
														{usersList?.length > 0 ? (
															usersList?.map((viewer: any, index: number) => (
																<Col
																	onClick={() => {
																		navigate(`/seeker/profile/${viewer?.userId}`);
																	}}
																	key={`${index}-${viewer?.userId}`}
																	xs={4}
																	className="text-center mb-3"
																>
																	<Image
																		src={
																			viewer?.profilePicture
																				? S3_URL + viewer?.profilePicture
																				: IMAGE_PATHS.UserDefault
																		}
																		roundedCircle
																		className="mb-2"
																		style={{
																			width: '60px',
																			height: '60px',
																			objectFit: 'cover',
																			border: `3px solid ${viewer.borderColor}`,
																		}}
																	/>
																	<p className="viewers-name mb-0">{viewer.name}</p>
																</Col>
															))
														) : (
															<Stack className="text-white">
																<p className="mb-0 text-white">
																	{translate('Social.noUserViewed')}
																</p>
															</Stack>
														)}
													</Row>
												</InfiniteScroll>
											</div>
										) : (
											<div className="viewers-list-body">
												<Stack className="text-white text-center p-3">
													<p className="mb-0 text-white">
														{translate('Common.loginRequired') ||
															'Please log in to view story viewers'}
													</p>
												</Stack>
											</div>
										)}
									</div>
								</div>
							);
						},
						seeMoreCollapsed: ({ toggleMore, action }: any) => (
							<div className="story-preview-box-bottom">
								{story?.userId === authUser?.userId ? (
									<>
										<span
											className="d-flex justify-content-center view-btn"
											onClick={() => {
												action('pause');
												setStoryId(story?.id);
												setCurrentPage((prev) => prev + 1);
												toggleMore('show');
											}}
										>
											<i className="ri-eye-fill"></i>
											{story?.totalViews || 0}
										</span>
										<div className="story-actions">
											<div className="edit-action">
												<Dropdown>
													<Dropdown.Toggle
														variant="success"
														id="dropdown-basic"
														className="p-0 border-0 rounded-circle"
													>
														<i className="ri-more-2-fill"></i>
													</Dropdown.Toggle>

													<Dropdown.Menu>
														<Dropdown.Item
															onClick={() => {
																action('pause');
																onDeleteStory(story?.id);
															}}
														>
															<i className="ri-delete-bin-7-line"></i> {translate('Common.delete')}
														</Dropdown.Item>
													</Dropdown.Menu>
												</Dropdown>
											</div>
										</div>
									</>
								) : (
									<div className="story-actions">
										<div className="edit-action">
											<Dropdown>
												<Dropdown.Toggle
													variant="success"
													id="dropdown-basic"
													className="p-0 border-0 rounded-circle"
												>
													<i className="ri-more-2-fill"></i>
												</Dropdown.Toggle>

												<Dropdown.Menu>
													<Dropdown.Item
														onClick={() => {
															action('pause');
															reportModal(story?.id);
														}}
													>
														<i className="ri-flag-line"></i> {translate('Report.report')}
													</Dropdown.Item>
												</Dropdown.Menu>
											</Dropdown>
										</div>
									</div>
								)}
							</div>
						),
					};
				});
			return storiesData || [];
		} else {
			return [];
		}
	};

	const reportStory = (values: any) => {
		reportProfileMutation({ ...values, contentId: Number(values?.contentId) }).then((res: any) => {
			hideCommonModal();
		});
	};

	const reportModal = (contentId: any) => {
		hideCommonModal();
		setModalConfig({
			visible: true,
			id: contentId,
			onClick: reportStory,
			type: 'reportFeeds',
			contentType: 'socialStory',
		});
	};

	// Don't render if there are no valid stories
	const storyArray = getStoryArr() || [];
	if (storyArray.length === 0) {
		return (
			<div className="story-preview-box-stories position-relative w-100 h-100 d-flex align-items-center justify-content-center">
				<p className="text-white">No stories available</p>
			</div>
		);
	}

	return (
		<div className="story-preview-box-stories position-relative w-100 h-100">
			{isActive ? (
				<>
					<button
						className="prev-story-btn p-0 bg-transparent border-0 h-100"
						type="button"
						onClick={onPrevious}
					></button>
					<button
						className="next-story-btn p-0 bg-transparent border-0 h-100"
						type="button"
						onClick={onNext}
					></button>

					<ReactInstaStories
						stories={storyArray}
						defaultInterval={1500}
						width={'100%'}
						height={'100%'}
						storyStyles={storyContent}
						loop={false}
						keyboardNavigation={true}
						isPaused={true}
						onStoryEnd={() => {
							resetStates();
						}}
						onAllStoriesEnd={onAllStoriesEndHandler}
						currentIndex={Math.max(0, Math.min(selectedIndex, storyArray.length - 1))}
						onStoryStart={onStoryStart}
					/>
				</>
			) : (
				<div className="story-preview-box">
					<div
						className="story-preview-box-coverpage position-relative h-100"
						onClick={() => customSlideChange(slideIndex)}
					>
						<Image
							className="h-100 w-100 object-fit-cover"
							src={
								storyData?.story[0]?.mediaType === 'video'
									? S3_SMALL_THUMB + getFileName(storyData?.story[0]?.mediaItem)
									: S3_URL + storyData?.story[0]?.mediaItem
							}
						/>
						<div className="story-preview-box-coverpage-info position-absolute top-50 start-50 translate-middle text-center">
							<Image
								className="story-preview-box-coverpage-info-img rounded-circle object-fit-cover"
								src={
									storyData?.profilePicture
										? S3_URL + storyData.profilePicture
										: IMAGE_PATHS.UserDefault
								}
							/>
							<h6 className="text-white">{storyData?.fullName}</h6>
							<p className="text-white mb-0">
								{getTimeDifferenceInBaseShortUnit(storyData?.story[0]?.createdAt)}
							</p>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default InstaStory;
